import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { priceService } from '@services/priceService';
import type { Asset } from '@shared/types';

// Using service-provided shape; no local types needed

export function useRealtimePrices(symbol: Asset) {
  const queryClient = useQueryClient();

  // Use React Query for loading state and cache management
  const query = useQuery({
    queryKey: ['realtimePrices', symbol],
    queryFn: async () => {
      await priceService.bootstrap([symbol]);
      return priceService.getRealtimePrices(symbol);
    },
    gcTime: 1000,
    staleTime: 1000,
  });

  useEffect(() => {
    queryClient.removeQueries({
      queryKey: ['realtimePrices', symbol],
      exact: true,
    });
    queryClient.invalidateQueries({
      queryKey: ['realtimePrices', symbol],
      exact: true,
      refetchType: 'all',
    });
  }, [symbol, queryClient]);

  // Subscribe to real-time price data updates and sync to React Query cache
  useEffect(() => {
    if (!query.isSuccess) {
      return;
    }
    const unsubscribe = priceService.subscribe(() => {
      try {
        const data = priceService.getRealtimePrices(symbol);
        queryClient.setQueryData(['realtimePrices', symbol], data);
      } catch (error) {
        console.warn(
          `Failed to update realtime price cache for ${symbol}:`,
          error,
        );
      }
    });

    return () => {
      unsubscribe();
    };
  }, [symbol, queryClient, query.isSuccess]);

  return query;
}
